import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Grid,
    TextField,
    Button as MUIButton,
    Typography,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Backdrop,
    CircularProgress,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import apiFetch from "../util/fetch-utils";
import { toaster } from "evergreen-ui";
import ExampleUsage from "./ExampleUsage";
import {
    RECOMMENDATION_POST_ENDED,
    RECOMMENDATION_POST_STARTED,
} from "../../reducers/addRecommendations";

const TableWrapper = styled(Paper)`
  margin-top: 16px;
  overflow-x: auto;
`;

const ActionsCol = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

function lineToRecommendation(line) {
    const parts = line.split("\t");
    return {
        srcUrl: parts[0],
        dstUrl: parts[1],
        title: parts[2],
        metaDescr: parts[3],
        H1: parts[4],
    };
}

const invalidDstUrl = (url) => {
    if (!url) return false;
    const dstUrl = url.trim();
    return (
        dstUrl &&
        dstUrl !== "" &&
        dstUrl !== "<REMOVE-RECOMMENDATION>" &&
        !isValidUrl(dstUrl)
    );
};

const isValidUrl = (string) => {
    try {
        // eslint-disable-next-line no-new
        new URL(string);
        return true;
    } catch (_) {
        return false;
    }
};

export default function CopyPasteSpecs() {
    const dispatch = useDispatch();
    const addRecommendations = useSelector((s) => s.addRecommendations);
    const workspace = useSelector((s) => s.workspaces?.current);

    const [uris, setUris] = useState("");

    const urisLines = useMemo(() => uris.split("\n"), [uris]);

    const handleChange = useCallback((e) => {
        setUris(e.target.value);
    }, []);

    const addMultipleUri = useCallback(
        async (event) => {
            event.preventDefault();
            dispatch({ type: RECOMMENDATION_POST_STARTED });

            const specs = uris
                .split("\n")
                .map((line) => line.split("\t"))
                .filter((parts) => parts[0] && parts[0].length > 0)
                .map((parts, index) => ({
                    rowIndex: index,
                    uri: parts[0],
                    destinationUri: parts[1],
                    title: parts[2],
                    metaDescription: parts[3],
                    h1: parts[4],
                }));

            try {
                const value = await apiFetch(
                    `/api/monitored-uris/add-recommendations?workspaceNumber=${workspace?.seqNumber}`,
                    {
                        body: JSON.stringify(specs),
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                    }
                );

                dispatch({ type: RECOMMENDATION_POST_ENDED });

                if (value.status === 200) {
                    toaster.success("Recommendations submitted successfully!");
                    setUris("");
                } else if (value.status >= 400) {
                    toaster.danger("Unable to submit recommendations.", {
                        description: `${value.status} ${value.statusText}`,
                        duration: 20,
                    });
                }
            } catch (err) {
                dispatch({ type: RECOMMENDATION_POST_ENDED });
                toaster.danger("Unable to submit recommendations.", {
                    description: String(err),
                    duration: 20,
                });
            }
        },
        [dispatch, uris, workspace?.seqNumber]
    );

    const showIndexCol = uris.length > 0;

    return (
        <Box>
            <Backdrop open={!!addRecommendations?.isPostInProgress} style={{ zIndex: 1300 }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <CircularProgress />
                    <Box mt={2}>
                        <Typography>Adding recommendations...</Typography>
                    </Box>
                </Box>
            </Backdrop>

            <ExampleUsage workspaceName={workspace?.name || ""} />

            <Box mt={1} mb={1}>
                <Typography variant="subtitle1">Paste from Excel</Typography>
            </Box>

            <form onSubmit={addMultipleUri}>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={10}>
                        <TextField
                            id="uris"
                            name="uris"
                            value={uris}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            variant="outlined"
                            minRows={8}
                            placeholder=""
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <ActionsCol>
                            <MUIButton
                                type="submit"
                                variant="contained"
                                color="primary"
                                disableElevation
                            >
                                Add
                            </MUIButton>
                        </ActionsCol>
                    </Grid>
                </Grid>
            </form>

            {uris.length > 0 &&
                urisLines.map((line, index) => {
                    const recomm = lineToRecommendation(line);
                    if (line.length > 0) {
                        if (!isValidUrl(recomm.srcUrl))
                            return (
                                <Alert key={`src-${index}`} severity="error">
                                    Invalid URL on line {index + 1}: <b>{recomm.srcUrl}</b>
                                </Alert>
                            );
                        if (invalidDstUrl(recomm.dstUrl))
                            return (
                                <Alert key={`dst-${index}`} severity="error">
                                    Invalid Destination URL on line {index + 1}: <b>{recomm.dstUrl}</b>
                                </Alert>
                            );
                    }
                    return null;
                })}

            <Box mt={2} mb={1}>
                <Typography variant="h6">Recommendations Preview</Typography>
            </Box>

            {uris.length > 0 && (
                <Alert severity="info">
                    You're about to add <b>{urisLines.length - 1}</b> recommendations
                </Alert>
            )}

            <TableWrapper variant="outlined">
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {showIndexCol && <TableCell width="5%">#</TableCell>}
                            <TableCell>URL</TableCell>
                            <TableCell>Dest. URL</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Meta Descr</TableCell>
                            <TableCell>H1</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {uris.length > 0 &&
                            urisLines.map((line, index) => {
                                const recomm = lineToRecommendation(line);
                                if (line.length > 0) {
                                    return (
                                        <TableRow key={index}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{recomm.srcUrl}</TableCell>
                                            <TableCell>{recomm.dstUrl}</TableCell>
                                            <TableCell>{recomm.title}</TableCell>
                                            <TableCell>{recomm.metaDescr}</TableCell>
                                            <TableCell>{recomm.H1}</TableCell>
                                        </TableRow>
                                    );
                                }
                                return null;
                            })}
                    </TableBody>
                </Table>
            </TableWrapper>
        </Box>
    );
}
